import { useEffect, useRef } from "react";
import { rng } from "./Sky";

/** The app's hyperdrive (140 seeded streaks out of the core, then a lavender flash), shortened for the web hand-off. */
const STREAKS = (() => {
  const rnd = rng(7);
  const colors = ["#FFFFFF", "#BFD8FF", "#C9A8FF", "#9FE8FF", "#FFE58A"];
  return Array.from({ length: 140 }, () => ({
    angle: rnd() * 2 * Math.PI,
    r0: 8 + rnd() * 120,
    speed: 0.6 + rnd() * 0.8,
    color: colors[Math.floor(rnd() * colors.length)]!,
    width: 0.8 + rnd() * 1.8,
    delay: rnd() * 0.25,
  }));
})();

const MS = 1300;

export function Warp({ x, y, onDone }: { x: number; y: number; onDone: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext("2d");
    if (!cv || !ctx) return;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const w = innerWidth;
    const h = innerHeight;
    cv.width = w * dpr;
    cv.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const k = Math.max(w / 390, h / 844);
    let raf = 0;
    const t0 = performance.now();
    const frame = (now: number) => {
      const v = Math.min(1, (now - t0) / MS);
      ctx.clearRect(0, 0, w, h);
      for (const s of STREAKS) {
        const t = Math.min(1, Math.max(0, (v - 0.02 - s.delay) / (0.9 - s.delay)));
        if (t <= 0) continue;
        const accel = Math.pow(t, 2.2);
        const head = (s.r0 + accel * 900 * s.speed) * k;
        const tail = Math.max(0, head - (4 + accel * 260 * s.speed) * k);
        const cos = Math.cos(s.angle);
        const sin = Math.sin(s.angle);
        const g = ctx.createLinearGradient(x + cos * tail, y + sin * tail, x + cos * head, y + sin * head);
        g.addColorStop(0, "rgba(255,255,255,0)");
        g.addColorStop(1, s.color);
        ctx.strokeStyle = g;
        ctx.globalAlpha = Math.min(1, t / 0.15);
        ctx.lineWidth = s.width * (1 + accel * 1.5);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(x + cos * tail, y + sin * tail);
        ctx.lineTo(x + cos * head, y + sin * head);
        ctx.stroke();
      }
      // Flash builds over the last third.
      const f = v < 0.62 ? 0 : Math.pow((v - 0.62) / 0.38, 1.6);
      ctx.globalAlpha = f;
      ctx.fillStyle = "#F4F1FF";
      ctx.fillRect(0, 0, w, h);
      ctx.globalAlpha = 1;
      if (v < 1) raf = requestAnimationFrame(frame);
      else done.current();
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [x, y]);

  return <canvas ref={ref} aria-hidden className="pointer-events-none fixed inset-0 z-[60] h-full w-full" />;
}
